import { STATUS_COLORS } from "@/pages/workOrders/components/statusConstants";
import type { CalendarEvent } from "@/shared/repositories/CalendarEventRepository";
import type { CalendarDayShift } from "@/shared/repositories/WorkOrderRepository";
import { getMomentTz } from "@dexteel/mesf-core";
import {
	eventDisplayName,
	getEventType,
	isSpellBreak,
} from "../config/eventTypes";

const moment = getMomentTz();

export const HOURS_PER_DAY = 24;
export const GRID_BORDER = "1px solid #e2e3e5";
// Equal-width hour columns so event blocks positioned by % align exactly
export const DAY_GRID_COLUMNS = `180px repeat(${HOURS_PER_DAY}, 1fr)`;
export const ROW_HEIGHT = 24;
export const EXTRA_LANE_HEIGHT = 16;

/** one line's row on a day board: identity plus the raw query slice it paints
 *  from. The heavy building happens in the row so it memoizes per line. */
export interface DayLine {
	assetId: number;
	assetName: string;
	slug: string;
	accentColor: string;
	events: CalendarEvent[] | undefined;
	dayShifts: CalendarDayShift[] | undefined;
	isLoading: boolean;
	errorMessage: string | null;
}

export interface RunSection {
	key: string;
	event: CalendarEvent;
	label: string;
	title: string;
	detail: string | null;
	colors: { bg: string; color: string };
	isSpell: boolean;
}

/** one event as a block positioned proportionally to its minutes */
export interface EventRun extends RunSection {
	leftPct: number;
	widthPct: number;
	/** began before this day — square that edge */
	clippedStart: boolean;
	/** continues past this day — square that edge */
	clippedEnd: boolean;
	/** vertical slot when runs overlap in time */
	lane: number;
}

export interface DayRuns {
	runs: EventRun[];
	laneCount: number;
	/** full-day Holiday/Downtime: the row renders as one striped block */
	fullDayBlock: RunSection | null;
}

const sectionKey = (ev: CalendarEvent) =>
	ev.EventId != null ? `ev-${ev.EventId}` : `wo-${ev.WorkOrderId}`;

const buildSection = (ev: CalendarEvent): RunSection => {
	if (ev.EventTypeCode === "W") {
		return {
			key: sectionKey(ev),
			event: ev,
			colors: STATUS_COLORS[ev.Status ?? "Pending"] ?? STATUS_COLORS.Pending,
			label: `${ev.PartNumber ?? "—"} ×${ev.Quantity ?? 0}`,
			title: `${ev.PartNumber ?? "—"} · ${ev.Status ?? "Pending"}`,
			detail: ev.Notes,
			isSpell: false,
		};
	}
	const type = getEventType(ev.EventTypeCode);
	const isSpell = isSpellBreak(ev);
	const name = eventDisplayName(ev);
	return {
		key: sectionKey(ev),
		event: ev,
		colors: { bg: type.bg, color: type.color },
		label: name,
		title: isSpell ? `${name} (line keeps running)` : name,
		detail: ev.Comments,
		isSpell,
	};
};

/** One line-day's events as minute-proportional blocks: a work order starting
 *  15:33 starts at 15:33 and unplanned time stays empty. Overlapping events
 *  stack into lanes. */
export const buildDayRuns = (
	events: readonly CalendarEvent[] | undefined,
	dateKey: string,
	tz: string,
): DayRuns => {
	const dayStart = moment.tz(dateKey, "YYYY-MM-DD", tz);
	const dayEnd = dayStart.clone().add(1, "day");
	// Measured, not assumed 24h: DST days are 23 or 25 hours wide
	const dayMs = dayEnd.diff(dayStart);
	const dayStartMs = dayStart.valueOf();
	const dayEndMs = dayEnd.valueOf();

	const sorted = [...(events ?? [])].sort(
		(a, b) =>
			moment.utc(a.Start).valueOf() - moment.utc(b.Start).valueOf() ||
			(a.Sequence ?? 0) - (b.Sequence ?? 0),
	);

	type Placed = { run: EventRun; startMs: number; endMs: number };
	const placed: Placed[] = [];
	const seen = new Set<string>();
	let fullDayBlock: RunSection | null = null;
	for (const ev of sorted) {
		const startMs = moment.utc(ev.Start).valueOf();
		const endMs = moment.utc(ev.End).valueOf();
		if (startMs >= dayEndMs || endMs <= dayStartMs) continue;

		// mill's fullDayNonProd generalized: a Holiday/Downtime covering the
		// whole day takes over the row (Holiday wins over Downtime)
		if (
			(ev.EventTypeCode === "H" || ev.EventTypeCode === "D") &&
			startMs <= dayStartMs &&
			endMs >= dayEndMs
		) {
			if (!fullDayBlock || ev.EventTypeCode === "H") {
				fullDayBlock = buildSection(ev);
			}
			continue;
		}

		const section = buildSection(ev);
		if (seen.has(section.key)) continue;
		seen.add(section.key);

		const clipStartMs = Math.max(startMs, dayStartMs);
		const clipEndMs = Math.min(endMs, dayEndMs);
		let lane = 0;
		while (
			placed.some(
				(p) =>
					p.run.lane === lane && clipStartMs < p.endMs && clipEndMs > p.startMs,
			)
		) {
			lane++;
		}
		placed.push({
			run: {
				...section,
				leftPct: ((clipStartMs - dayStartMs) / dayMs) * 100,
				widthPct: ((clipEndMs - clipStartMs) / dayMs) * 100,
				clippedStart: startMs < dayStartMs,
				clippedEnd: endMs > dayEndMs,
				lane,
			},
			startMs: clipStartMs,
			endMs: clipEndMs,
		});
	}

	const runs = placed.map((p) => p.run);
	const laneCount = runs.reduce((max, r) => Math.max(max, r.lane + 1), 0);
	return { runs, laneCount, fullDayBlock };
};
